import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { initialPlaybooks } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import { FiCheckCircle, FiAlertTriangle, FiClock, FiPlay, FiFilter, FiInbox } from 'react-icons/fi';

const Playbooks = () => {
  const [playbooks, setPlaybooks] = useState(initialPlaybooks);
  const [activeTab, setActiveTab] = useState('active');

  const tabs = [
    { id: 'active', label: 'In Progress', icon: FiClock },
    { id: 'warning', label: 'Needs Attention', icon: FiAlertTriangle },
    { id: 'success', label: 'Completed', icon: FiCheckCircle },
    { id: 'archived', label: 'Archived (Empty State)', icon: FiInbox }
  ];

  const filteredPlaybooks = playbooks.filter(p => activeTab === 'archived' ? false : p.status === activeTab);

  // Local simulated interaction - advance the state of a playbook
  const advancePlaybookState = (id) => {
    setPlaybooks(current => current.map(p => {
      if (p.id === id) {
        if (p.status === 'warning') return { ...p, status: 'active', progress: Math.min(p.progress + 20, 99) };
        if (p.status === 'active') return { ...p, status: 'success', progress: 100 };
      }
      return p;
    }));
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Playbooks & Simulated States</h1>
          <p className="text-slate-400 mt-2">Manage intervention workflows and explore interactive UI states.</p>
        </div>
        <button className="px-4 py-2 bg-[#13131a] border border-white/10 hover:bg-white/5 text-slate-300 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
          <SafeIcon icon={FiFilter} /> Filter Workflows
        </button>
      </div>

      {/* Tabs - Interactive State Selector */}
      <div className="flex space-x-1 p-1 bg-[#13131a] border border-white/5 rounded-xl w-full max-w-2xl overflow-x-auto custom-scrollbar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-violet-500/20 text-violet-300 shadow-[0_0_10px_rgba(139,92,246,0.1)] border border-violet-500/30'
                : 'text-slate-400 hover:text-slate-200 hover:bg-white/5 border border-transparent'
            }`}
          >
            <SafeIcon icon={tab.icon} />
            {tab.label}
            {tab.id !== 'archived' && (
              <span className="ml-2 bg-white/10 px-2 py-0.5 rounded-full text-xs">
                {playbooks.filter(p => p.status === tab.id).length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="min-h-[400px]">
        <AnimatePresence mode="wait">
          {filteredPlaybooks.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="h-[400px] bg-[#13131a] border border-white/5 border-dashed rounded-2xl flex flex-col items-center justify-center text-center p-8"
            >
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-4 border border-white/10">
                <SafeIcon icon={FiInbox} className="text-2xl text-slate-500" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No Playbooks Found</h3>
              <p className="text-slate-400 max-w-sm">
                This is a simulated <strong className="text-violet-400">Empty State</strong>. There are currently no workflows matching this specific status filter.
              </p>
              <button onClick={() => setActiveTab('active')} className="mt-6 px-4 py-2 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-sm font-medium transition-colors border border-white/10">
                View Active Playbooks
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
            >
              {filteredPlaybooks.map((playbook) => (
                <motion.div
                  key={playbook.id}
                  layout
                  className={`p-6 rounded-2xl border transition-all ${
                    playbook.status === 'warning' 
                      ? 'bg-rose-500/5 border-rose-500/20' 
                      : playbook.status === 'success'
                      ? 'bg-emerald-500/5 border-emerald-500/20'
                      : 'bg-[#13131a] border-white/5 hover:border-violet-500/30'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {playbook.status === 'warning' && <SafeIcon icon={FiAlertTriangle} className="text-rose-400" />}
                        {playbook.status === 'success' && <SafeIcon icon={FiCheckCircle} className="text-emerald-400" />}
                        {playbook.status === 'active' && <SafeIcon icon={FiClock} className="text-violet-400" />}
                        <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{playbook.type} Playbook</span>
                      </div>
                      <h3 className="text-xl font-semibold text-white">{playbook.title}</h3>
                      <p className="text-slate-400 text-sm mt-1">Account: <span className="text-slate-200">{playbook.account}</span></p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                      playbook.status === 'warning' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' :
                      playbook.status === 'success' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                      'bg-violet-500/10 text-violet-400 border-violet-500/20'
                    }`}>
                      {playbook.status === 'warning' ? 'Needs Attention' : playbook.status === 'success' ? 'Completed' : 'In Progress'}
                    </span>
                  </div>

                  <div className="mb-6">
                    <div className="flex justify-between items-end mb-2">
                      <span className="text-xs text-slate-500">Progress</span>
                      <span className="text-sm font-mono text-slate-300">{playbook.progress}%</span>
                    </div>
                    <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${playbook.progress}%` }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className={`h-full rounded-full ${
                          playbook.status === 'warning' ? 'bg-rose-500' :
                          playbook.status === 'success' ? 'bg-emerald-500' :
                          'bg-violet-500'
                        }`}
                      />
                    </div>
                  </div>

                  <div className="bg-black/20 p-4 rounded-xl border border-white/5 mb-6">
                    <p className="text-sm text-slate-300"><strong className="text-slate-500">Latest Note:</strong> {playbook.notes}</p>
                    <p className="text-xs text-slate-500 mt-2">Updated {playbook.lastUpdated}</p>
                  </div>

                  {playbook.status !== 'success' && (
                    <div className="flex gap-3">
                      <button 
                        onClick={() => advancePlaybookState(playbook.id)}
                        className="flex-1 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(139,92,246,0.2)]"
                      >
                        <SafeIcon icon={FiPlay} />
                        Simulate {playbook.status === 'warning' ? 'Resolution' : 'Completion'}
                      </button>
                    </div>
                  )}
                  {playbook.status === 'success' && (
                    <div className="flex gap-3">
                      <button className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2">
                        View Post-Mortem Report
                      </button>
                    </div>
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Playbooks;